/**
 * Fake Line Item Properties Manager
 *
 * Manages product properties via browser localStorage instead of native Shopify line item properties.
 * Properties are displayed in cart/mini-cart but NOT sent to checkout.
 *
 * Storage structure:
 * {
 *   "variant_id:key": "value"
 * }
 */

class FakePropertiesManager {
  constructor() {
    this.storageKey = "ezup_fake_properties";
    this.refreshTimeout = null;
    this.isRefreshing = false; // Flag to prevent re-entrant refreshes
    this.lastRefreshTime = 0; // Track last refresh timestamp
    this.minRefreshInterval = 500; // Minimum time between refreshes (ms)
    this.init();
  }

  init() {
    // Listen for product:added event (fired by theme after successful add to cart)
    document.addEventListener("product:added", (event) => {
      console.log("Product added event detected", event);
    });

    // Listen for variant changes to update data-variant-id on inputs
    document.addEventListener("variant:changed", (event) =>
      this.handleVariantChange(event)
    );

    // Intercept add to cart button clicks to capture properties BEFORE submission
    document.addEventListener(
      "click",
      (event) => {
        const addToCartButton = event.target.closest(
          '[data-action="add-to-cart"]'
        );
        if (addToCartButton) {
          this.capturePropertiesFromButton(addToCartButton);
        }
      },
      true
    ); // Use capture phase to run before theme handler

    // Initial cleanup on page load
    this.cleanupOrphanedProperties();
  }

  getPropertiesForDisplay(variantId) {
    return this.getPropertiesForVariant(variantId);
  }
  /**
   * Render properties for mini-cart
   */
  renderMiniCartProperties(container, variantId) {
    if (!variantId) return;

    const properties = this.getPropertiesForDisplay(variantId);
    console.log(
      "Mini-cart: Rendering properties for variant",
      variantId,
      properties
    );

    if (properties.length === 0) {
      container.innerHTML = "";
      return;
    }

    const visibleProperties = properties.filter((prop) => {
      const firstChar = prop.first.charAt(0);
      return (
        firstChar !== "_" &&
        prop.first !== "Email" &&
        prop.first !== "Logo" &&
        prop.last &&
        prop.last.trim() !== ""
      );
    });

    if (visibleProperties.length === 0) {
      container.innerHTML = "";
      return;
    }

    let html = '<ul class="mini-cart__property-list" role="list">';
    let detailsOpen = false;

    visibleProperties.forEach((prop) => {
      if (prop.first === "SKU") {
        html += `
          <li class="mini-cart__property mini-cart__property--sku">
            <span class="mini-cart__property-label">${prop.first}:</span>
            <span class="mini-cart__property-value">${prop.last}</span>
          </li>
        `;
      } else {
        if (!detailsOpen) {
          html += `
            <details class="mini-cart__properties-details">
              <summary class="mini-cart__properties-summary">
                <span class="mini-cart__properties-summary-text">See details</span>
                <span class="mini-cart__properties-summary-icon"><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon--arrow-bottom" viewBox="0 0 12 8"><path d="M1.2 1.2l4.8 4.8 4.8-4.8" stroke="currentColor" stroke-width="2" fill="none"/></svg></span>
              </summary>
              <div class="mini-cart__properties-group">
          `;
          detailsOpen = true;
        }

        const parts = prop.last.split("-");
        let longestPart = prop.last;
        let longestLength = 0;

        parts.forEach((part) => {
          if (part.length > longestLength) {
            longestPart = part;
            longestLength = part.length;
          }
        });

        html += `
          <li class="mini-cart__property">
            <span class="mini-cart__property-label">${prop.first}:</span>
            <span class="mini-cart__property-value">${longestPart}</span>
          </li>
        `;
      }
    });

    if (detailsOpen) {
      html += "</div></details>";
    }

    html += "</ul>";
    container.innerHTML = html;
    console.log("Mini-cart properties rendered successfully");
  }

  /**
   * Capture properties from fake property inputs when clicking add to cart button
   */
  capturePropertiesFromButton(button) {
    const form = button.closest('form[action*="/cart/add"]');
    if (!form) {
      console.warn("Add to cart button not inside a cart form");
      return;
    }

    console.log("Capturing properties from form:", form);

    const fakeInputs = form.querySelectorAll(".fake-property-input");
    console.log("Found fake property inputs:", fakeInputs.length);

    fakeInputs.forEach((input) => {
      const propertyName = input.getAttribute("data-fake-property");
      const variantId = input.getAttribute("data-variant-id");
      const value = input.value;

      console.log("Processing input:", { propertyName, variantId, value });

      if (propertyName && variantId && value) {
        this.setProperty(variantId, propertyName, value);
        console.log(
          `Saved fake property: ${variantId}:${propertyName} = ${value}`
        );
      }
    });

    // Log current storage state
    console.log("Current fake properties storage:", this.getAllProperties());
  }

  /**
   * Capture properties from fake property inputs (legacy method for events)
   */
  captureProperties(event) {
    const form = event.target.closest("form");
    if (!form) return;

    const fakeInputs = form.querySelectorAll(".fake-property-input");
    fakeInputs.forEach((input) => {
      const propertyName = input.getAttribute("data-fake-property");
      const variantId = input.getAttribute("data-variant-id");
      const value = input.value;

      if (propertyName && variantId && value) {
        this.setProperty(variantId, propertyName, value);
      }
    });
  }

  /**
   * Update data-variant-id on fake property inputs when variant changes
   */
  handleVariantChange(event) {
    const { variant } = event.detail;
    if (!variant || !variant.id) return;

    const form =
      event.target.closest("form") ||
      document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const fakeInputs = form.querySelectorAll(".fake-property-input");
    fakeInputs.forEach((input) => {
      input.setAttribute("data-variant-id", variant.id);

      // Update option values
      if (input.classList.contains("product-property__option-1")) {
        input.value = variant.option1 || "";
      } else if (input.classList.contains("product-property__option-2")) {
        input.value = variant.option2 || "";
      } else if (input.classList.contains("product-property__option-3")) {
        input.value = variant.option3 || "";
      } else if (input.classList.contains("product-property__sku")) {
        input.value = variant.sku || "";
      }
    });
  }

  /**
   * Get storage key for a specific variant and property
   */
  getStorageKey(variantId, propertyName) {
    return `${variantId}:${propertyName}`;
  }

  /**
   * Get all properties from localStorage
   */
  getAllProperties() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : {};
    } catch (e) {
      console.error("Error reading fake properties:", e);
      return {};
    }
  }

  /**
   * Save all properties to localStorage
   */
  saveAllProperties(properties) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(properties));
    } catch (e) {
      console.error("Error saving fake properties:", e);
    }
  }

  /**
   * Set a property value for a specific variant
   */
  setProperty(variantId, propertyName, value) {
    const properties = this.getAllProperties();
    const key = this.getStorageKey(variantId, propertyName);

    if (value && value.trim() !== "") {
      properties[key] = value;
    } else {
      delete properties[key];
    }

    this.saveAllProperties(properties);
  }

  /**
   * Get a property value for a specific variant
   */
  getProperty(variantId, propertyName) {
    const properties = this.getAllProperties();
    const key = this.getStorageKey(variantId, propertyName);
    return properties[key] || null;
  }

  /**
   * Get all properties for a specific variant
   */
  getPropertiesForVariant(variantId) {
    const allProperties = this.getAllProperties();
    const variantProperties = {};
    const prefix = `${variantId}:`;

    Object.keys(allProperties).forEach((key) => {
      if (key.startsWith(prefix)) {
        const propertyName = key.substring(prefix.length);
        variantProperties[propertyName] = allProperties[key];
      }
    });

    return variantProperties;
  }

  /**
   * Remove all properties for a specific variant
   */
  removePropertiesForVariant(variantId) {
    const allProperties = this.getAllProperties();
    const prefix = `${variantId}:`;

    Object.keys(allProperties).forEach((key) => {
      if (key.startsWith(prefix)) {
        delete allProperties[key];
      }
    });

    this.saveAllProperties(allProperties);
  }

  /**
   * Clean up properties for variants no longer in cart
   */
  async cleanupOrphanedProperties() {
    try {
      // Fetch current cart
      const response = await fetch("/cart.js");
      const cart = await response.json();

      // Get all variant IDs currently in cart
      const cartVariantIds = new Set(
        cart.items.map((item) => item.variant_id.toString())
      );

      // Remove properties for variants not in cart
      const allProperties = this.getAllProperties();
      let hasChanges = false;

      Object.keys(allProperties).forEach((key) => {
        const variantId = key.split(":")[0];
        if (!cartVariantIds.has(variantId)) {
          delete allProperties[key];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.saveAllProperties(allProperties);
      }
    } catch (e) {
      console.error("Error cleaning up fake properties:", e);
    }
  }

  /**
   * Clear all stored properties
   */
  clearAll() {
    localStorage.removeItem(this.storageKey);
  }

  /**
   * Get properties formatted for display (like Shopify's line_item.properties)
   */
  getPropertiesForDisplay(variantId) {
    const properties = this.getPropertiesForVariant(variantId);

    // Convert to array format similar to Shopify's properties
    return Object.entries(properties).map(([key, value]) => ({
      first: key,
      last: value,
    }));
  }
}

// Initialize global instance
if (
  !window.fakePropertiesManager ||
  window.fakePropertiesManager === undefined
) {
  window.fakePropertiesManager = new FakePropertiesManager();
}
